/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import modalClose from '@brands/assets/icons/close.svg';
import Button from '@brands/Components/Button/Button';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import useAuditTrail from '@brands/hooks/useAuditTrail';
import useDebounce from '@brands/hooks/useDebounce';
import { formatDate } from '@brands/Utils/formatDate';
import { triggerAuditTrailCsvDownload } from '@brands/Utils/triggerAuditTrailCsvDownload';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';

import downloadCSV from './downloadCSV.svg';
import { sortData, SortKeys, SortOrder } from './sortUserData';
import styles from './styles.module.scss';

type CaseActivityModalProps = {
  case_id: number;
  setOpenCaseActivityModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const CaseActivityModal = ({ case_id, setOpenCaseActivityModal }: CaseActivityModalProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const lastAuditTrailItemRef = useRef<HTMLTableRowElement>(null);

  const params = useMemo(() => {
    const baseParams = {
      case_id,
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      limit: 15,
    };

    return {
      ...baseParams,
    };
  }, [case_id]);

  const debouncedParams = useDebounce(params, 1000);

  const { auditTrailList, loadNextPage, dataLoading } = useAuditTrail(debouncedParams);

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().then(() => {
      setIsLoading(false);
    });
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const lastItem = entries[0];
    if (lastItem.isIntersecting && !isLoading) {
      loadMore();
    }
  };

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(handleIntersection);
    if (lastAuditTrailItemRef.current) observer.current.observe(lastAuditTrailItemRef.current);
  }, [auditTrailList]);

  const headers: { key: SortKeys; label: string; isSorted: boolean }[] = [
    { key: 'actor', label: 'Name', isSorted: false },
    { key: 'action_message', label: 'Action', isSorted: false },
    { key: 'created_at', label: 'Modification date', isSorted: false },
  ];

  const [headersState, setHeadersState] = useState(headers);
  const [sortKey, setSortKey] = useState<SortKeys>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const sortedData = useCallback(
    () => sortData({ tableData: auditTrailList, sortKey, reverse: sortOrder === 'desc' }),
    [auditTrailList, sortKey, sortOrder],
  );

  const changeSort = (key: SortKeys): void => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortKey(key);
    setHeadersState(
      headersState.map((header) => {
        if (sortKey === key) {
          return { ...header, isSorted: !header.isSorted };
        }
        return { ...header, isSorted: false };
      }),
    );
  };

  const handleDownloadClick = async (): Promise<void> => {
    setIsLoading(true);
    await triggerAuditTrailCsvDownload(debouncedParams);
    setIsLoading(false);
  };

  return (
    <div className={styles.modalBackground}>
      <div className={`${styles.modalContainer} ${styles.auditTrailDiv}`}>
        <div className={styles.closeICon}>
          <img src={modalClose} onClick={() => setOpenCaseActivityModal(false)} alt="close" />
        </div>
        <div className={styles.ccDashboardBody}>
          {!dataLoading ? (
            <div className={styles.tableContainer}>
              <table className={styles.allCasesTable}>
                <thead className={`${styles.providerListTableHeader} ${styles.dashboardHeaderName} ${styles.titles}`}>
                  <tr>
                    {headersState.map((row) => {
                      return (
                        <th key={row.key} onClick={() => changeSort(row.key)}>
                          <span className={`fs-unmask ${sortKey === row.key ? styles.bold : ''}`}>
                            {row.label}
                            {row.label !== '' ? sortOrder === 'asc' ? <HiChevronDown /> : <HiChevronUp /> : ''}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                {auditTrailList.length > 0 ? (
                  <tbody>
                    {sortedData().map((auditTrailRecord) => {
                      return (
                        <Fragment key={auditTrailRecord.id}>
                          <tr className={`${styles.providerListTableBody}`}>
                            <td className={`${styles.mobileDue} `}>
                              <div className={`${styles.due} fs-exclude`}>
                                <span className={styles.mobileHeader}>Name</span>
                                <span className={styles.mobileDueDate}>
                                  {auditTrailRecord.actor?.full_name} ({auditTrailRecord.actor?.role?.name})
                                </span>
                              </div>
                            </td>
                            <td className={`${styles.mobileDue} `}>
                              <div className={`${styles.due} fs-exclude`}>
                                <span className={styles.mobileHeader}>Action</span>
                                <span className={styles.mobileDueDate}>{auditTrailRecord.action_message}</span>
                              </div>
                            </td>
                            <td className={`${styles.mobileDue}`}>
                              <div className={`${styles.due} fs-unmask`}>
                                <span className={styles.mobileHeader}>Modification date</span>
                                <span className={styles.mobileDueDate}>{formatDate(auditTrailRecord.created_at)}</span>
                              </div>
                            </td>
                          </tr>
                          <tr className={styles.noStartTime} key={`${auditTrailRecord.id}-3`}>
                            <div>
                              <span />
                            </div>
                          </tr>
                        </Fragment>
                      );
                    })}
                    <tr className={styles.lastItemRef} ref={lastAuditTrailItemRef} />
                  </tbody>
                ) : (
                  <span className={styles.noData}>No data to display</span>
                )}
              </table>
              {isLoading && <Loading dotted />}
            </div>
          ) : (
            <span>
              <Loading dotted />
            </span>
          )}
        </div>
        <div className={styles.downloadCSVButton}>
          <Button onClick={handleDownloadClick} disabled={isLoading}>
            <img src={downloadCSV} alt="download" />
            Download CSV
          </Button>
        </div>
      </div>
    </div>
  );
};
export default CaseActivityModal;
